import { Popover, Tooltip } from "@mui/material";
import "./filterManager.css";
import { useRef, useState } from "react";

//example of how to use this componet 

// const rawFilters = {--> create a base object for the config , inside you need  to create an internal property and data for each filter (in this example we have 3 filters in locations configuration section).

//   name:--> { the name of the property will be the same columnName than you sent to the backend in the filters.

//     selected:--> locationSelected, state where you storage the values selected of the corresponding filter.

//     setSelected:--> setLocationSelected, setter method of the previus mencioned state.

//     setPreselect:--> setPreselectLocation, state with preselection if you need.

//     setToDelete:--> setLocationOptionToDelete,  state where you will storage the value that you will remove individually from the filter
//     getDisplay: (item) => item.code,--> the value that you wil show in the list of filters
//   },

//   code: {
//     selected: iataSelected,
//     setSelected: setIataSelected,
//     setPreselect: setPreselectIata,
//     setToDelete: setIataOptionToDelete,
//     getDisplay: (item) => item.standard_code,
//   },
//   timezone: {
//     selected: timeZoneSelected,
//     setSelected: setTimeZoneSelected,
//     setPreselect: setPreselectTimezone,
//     setToDelete: setTimezoneOptionToDelete,
//     getDisplay: (item) => item.timezone_id,
//   },
// };


// const filtersConfig = createFiltersConfig(
//   rawFilters,--> object created previusly
//   setBody,--> state where you storage the filters than you sent to the backend
//   getAllLocations, --> function than execute the backend petition to get your data
//   [0, pageSize, searchValue, false]--> parameter than need the function to get your data from backend !important => filter will assign the body with filter at the end in your function set the parameter of body at the end
// );





/**
 * The function createFiltersConfig generates a configuration object for managing filters in a React
 * application.
 * @returns The `createFiltersConfig` function returns a configuration object that contains settings
 * and functions for handling filters. The configuration object is created based on the input filters
 * object and callbacks provided to the function. Each filter in the configuration object includes
 * properties such as label, getDisplayValue function, clearGroup function, and deleteValue function.
 */
export const createFiltersConfig = (
  filters,
  setBodyCallback,
  getDataCallback,
  dataFetchArgs
) => {
  const config = {};

  for (const columnName in filters) {
    const {
      selected,
      setSelected,
      setPreselect,
      setToDelete,
      getDisplay,
    } = filters[columnName];

    config[columnName] = {
      label: columnName.replace(/_/g, " "),

      getDisplayValue: (value) => {
        if (typeof value === "object" && value !== null) {
          return getDisplay(value);
        }

        if (!selected || selected.length === 0) {
          return ""; // o "" si prefieres que no muestre nada
        }

        const option = selected.find((item) =>
          item.id === value ||
          item.name === value ||
          item.timezone_id === value ||
          item.standard_code === value ||
          item.code === value
        );

        return getDisplay(option) || value;
      },

      clearGroup: () => {
        setPreselect?.(null);
        setBodyCallback((prev) => {
          const updated = prev.filter((f) => f.columnName !== columnName);
          getDataCallback(...dataFetchArgs, updated);
          return updated;
        });
      },

      deleteValue: (value) => {
        if (setSelected) {
          setSelected((prev) =>
            prev.filter((item) =>
              ![item.id, item.name, item.code, item.part_number, item.standard_code, item.timezone_id, item.status, item.tail_number].includes(value)
            )
          );
        }

        setToDelete?.(value);

        setBodyCallback((prev) => {
          const updated = prev
            .map((item) => {
              if (item.columnName === columnName) {
                const updatedValues = item.value.filter(
                  (val) => val !== value
                );
                return updatedValues.length > 0
                  ? { ...item, value: updatedValues }
                  : null;
              }
              return item;
            })
            .filter(Boolean);

          getDataCallback(...dataFetchArgs, updated);
          return updated;
        });
      },
    };
  }

  return config;
};



/* The above code is a React component called `FiltersManager` that manages filters for a UI component.
It takes in various props such as `appliedFilters`, `excludeColumns`, `onFilterChange`,
`handleClearAllFilters`, `onClearGroup`, `onDeleteFilter`, `getFilterLabel`, `getDisplayValue`,
`onButtonClick`, and `formatDate`. */

export const FiltersManager = ({
  appliedFilters,
  excludeColumns = [],
  onFilterChange,
  handleClearAllFilters,
  onClearGroup,
  onDeleteFilter,
  getFilterLabel,
  getDisplayValue,
  onButtonClick,
  formatDate = (dateString) => new Date(dateString).toLocaleDateString(),
}) => {
  const safeAppliedFilters = Array.isArray(appliedFilters) ? appliedFilters : [];
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);

  const openMenu = (event) => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const shouldDisplayFilter = (columnName) => {
    return !excludeColumns.includes(columnName);
  };

  /**
   * The `handleDeleteFilter` function removes a specific value from a filter in a list of applied
   * filters and triggers a filter change event if a deletion occurs.
   */
  const handleDeleteFilter = (columnName, value) => {
    if (onDeleteFilter) {
      onDeleteFilter(columnName, value);
    } else {
      const updatedFilters = appliedFilters.map((filter) => {
        if (filter.columnName === columnName) {
          if (Array.isArray(filter.value)) {
            return {
              ...filter,
              value: filter.value.filter((val) => val !== value),
            };
          }
          return null;
        }
        return filter;
      }).filter(Boolean);
      onFilterChange(updatedFilters);
    }
  };

  /**
   * The `handleClearGroup` function checks if `onClearGroup` is defined and calls it with the
   * `columnName` parameter, otherwise it filters out the applied filters based on the `columnName` and
   * calls `onFilterChange` with the updated filters.
   */
  const handleClearGroup = (columnName) => {
    if (onClearGroup) {
      onClearGroup(columnName);
    } else {
      const updatedFilters = appliedFilters.filter(
        (filter) => filter.columnName !== columnName
      );
      onFilterChange(updatedFilters);
    }
  };

  /* This code snippet is creating a new array called `filteredFilters` by filtering the `appliedFilters`
  array based on certain conditions. */
  const filteredFilters = Array.isArray(appliedFilters)
    ? appliedFilters.filter(filter =>
      filter?.columnName && !excludeColumns.includes(filter.columnName)
    )
    : [];


  const activeFiltersCount = filteredFilters.length;

  return (
    <>
      <Tooltip title="Filters" arrow >
        <button ref={buttonRef} onClick={onButtonClick} className="filter-manager-btn">
          <i className="icon-Filter_manager"></i>
          {activeFiltersCount > 0 && (
            <span className="filter-manager-count-badge"
              onClick={(e) => {
                e.stopPropagation()
                openMenu(e)
              }}
            >
              {activeFiltersCount}
            </span>
          )}
        </button></Tooltip>
      {
        activeFiltersCount > 0 && (
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            disableRestoreFocus
          >
            <div className="orders-header-filters-applied">
              <nav>
                <p>Applied Filters</p>
                <span onClick={handleClearAllFilters}>Clear All</span>
              </nav>
              <section className="applied-filters-container">
                {safeAppliedFilters.filter((filter) => shouldDisplayFilter(filter.columnName))
                  .map((item) => (
                    <div key={item.columnName} className="filter-group">
                      <div className="filter-group-title">
                        {getFilterLabel(item.columnName)}
                        <p onClick={() => handleClearGroup(item.columnName)}>Clear</p>
                        <span className="filter-group-line"></span>
                      </div>

                      <div className="filter-values">
                        {Array.isArray(item.value) ? (
                          item.value.map((val, index) => (

                            <span
                              key={`${item.columnName}-${val}-${index}`}
                              className="filter-value"
                            >
                              {getDisplayValue(item.columnName, val)}
                              <i
                                className="icon-cross"
                                onClick={() => handleDeleteFilter(item.columnName, val)}
                              ></i>
                            </span>
                          ))
                        ) : (
                          <span className="filter-value">
                            {item.value.startDate
                              ? `${formatDate(item.value.startDate)} to ${formatDate(item.value.endDate)}`
                              : "Date range"}
                            <i
                              className="icon-cross"
                              onClick={() => handleDeleteFilter(item.columnName, item.value)}
                            ></i>
                          </span>
                        )}

                      </div>
                    </div>
                  ))}
              </section>
            </div>
          </Popover>
        )
      }
    </>
  );
};